<script lang="ts">
  import SearchInput from '@/components/search-input/SearchInput.svelte';
  import {IntegrationService} from '@/services';
  import {Integration, IntegrationUser, SCTVMedia} from '@/types/integrations';
  import {debounce} from '@/utils/common';
  import Swal from 'sweetalert2';

  export let integration: Integration;
  export let userIntegration: IntegrationUser | undefined;
  export let isUploadIntegrationsDialogOpen: boolean;
  export let isUploading: boolean;

  let identifier: string = '';
  let errorMessage: string = '';
  let searchTerm: string;
  let isLinking: boolean = false;
  let toggleDropdown: boolean = false;
  let videos: Array<SCTVMedia> = [];
  let allVideos: Array<SCTVMedia> = [];
  let activeMedia: SCTVMedia;

  const integrationService = new IntegrationService();

  async function linkAccount() {
    isLinking = true;
    errorMessage = '';
    await integrationService
      .link(identifier, integration.id)
      .then(response => {
        if (response?.status == 404) {
          errorMessage =
            'We cannot find that account. Please check your email address. If you think this is a mistake, please let us know.';
        } else {
          userIntegration = response;
          Swal.fire({
            position: 'bottom-end',
            icon: 'success',
            title: 'Account Linked',
            showConfirmButton: false,
            timer: 1500,
            toast: true
          });
          identifier = '';
        }
        isLinking = false;
      })
      .catch(error => {
        errorMessage = 'Failed Linking Account';
        isLinking = false;
        Swal.fire({
          position: 'bottom-end',
          icon: 'error',
          title: 'Failed to Link',
          showConfirmButton: false,
          timer: 1500,
          toast: true
        });
      });
  }

  const handleSearch = (e: any) => {
    debounce(e, filter);
  };

  async function filter(e: any) {
    searchTerm = e.target.value;
    if (searchTerm != '') {
      videos = videos.filter(video => {
        return video.file_name.includes(searchTerm);
      });
    } else {
      videos = allVideos;
    }
  }

  async function unlinkAccount(id: number | undefined) {
    toggleDropdown = !toggleDropdown;
    return await integrationService.unlink(id).then(() => {
      userIntegration = undefined;
      videos = [];
      allVideos = videos;
    });
  }

  async function getUserMedia(id: number | undefined) {
    videos = await integrationService.getIntegrationMedia(id);
    allVideos = videos;
  }

  async function selectMedia(media: SCTVMedia) {
    activeMedia = media;
  }

  async function downloadMedia(media: SCTVMedia) {
    if (userIntegration != undefined) {
      isUploading = true;
      isUploadIntegrationsDialogOpen = false;
      await integrationService.downloadMedia(userIntegration?.id, activeMedia);
    }
  }

  $: if (userIntegration?.id) {
    getUserMedia(userIntegration?.id);
  }

  $: displayClass = toggleDropdown ? 'w-full absolute bottom-full rounded-xl border-y border bg-white z-10' : 'hidden';
</script>

<div class="flex p-6 text-black dark:text-zinc-200">
  {#if userIntegration != undefined && userIntegration?.id != null}
    <div class="flex w-full flex-row items-center justify-between space-x-4">
      <p class="w-full text-xl font-bold text-gray-800">Media Library</p>
      <div class="w-full p-1">
        <SearchInput placeholder="Search for a Clip name" on:input={e => handleSearch(e)} />
      </div>
      <button
        on:click={() => (isUploadIntegrationsDialogOpen = false)}
        class="ml-auto rounded-full px-2 hover:bg-zinc-100"
      >
        <i class="fa-solid fa-xmark p-2 text-lg text-black" aria-hidden="true"></i>
      </button>
    </div>
  {:else}
    <div class="flex items-center gap-2 py-1.5">
      <h1 class="text-3xl font-bold">Upload from</h1>
      <span
        ><a href="https://streamingchurch.tv/cgi-bin/affiliate.pl?id=sermonshots" target="_blank"
          ><img src={integration?.logo} alt={integration?.name} /></a
        ></span
      >
    </div>
    <button
      on:click={() => (isUploadIntegrationsDialogOpen = false)}
      class="ml-auto rounded-full px-2 hover:bg-zinc-100"
    >
      <i class="fa-solid fa-xmark p-2 text-lg text-black" aria-hidden="true"></i>
    </button>
  {/if}
</div>

{#if userIntegration != undefined && userIntegration?.id != null}
  <div class="flex max-h-[500px] min-h-[300px] overflow-y-auto p-4">
    <div class="grid grid-cols-3 gap-4">
      {#if videos.length != 0}
        {#each videos as video}
          <div
            class="flex cursor-pointer flex-col rounded-lg bg-white"
            on:click={() => selectMedia(video)}
            on:keydown={() => selectMedia(video)}
          >
            <img
              class="aspect-video w-full rounded-lg border-2 object-cover transition-all hover:border-secondary {activeMedia?.file_name ===
              video?.file_name
                ? 'border-2 border-primary'
                : ''}"
              src={video?.thumbnail_url}
              alt={video?.file_name}
            />
            <p class="truncate p-2 text-sm font-semibold text-dark dark:text-zinc-200">{video?.file_name}</p>
            {#if activeMedia?.file_name === video?.file_name}
              <span class="block p-2">
                <button
                  type="button"
                  on:click={() => downloadMedia(video)}
                  class="hoversermonbg w-full rounded-md bg-primary px-4 py-2 font-bold text-zinc-100"
                >
                  Upload
                </button>
              </span>
            {/if}
          </div>
        {/each}
      {/if}
    </div>
  </div>
  <hr class="my-2 p-2" />
  <div class="flex justify-between space-x-4 px-2">
    <a href="https://streamingchurch.tv/cgi-bin/affiliate.pl?id=sermonshots" target="_blank">
      <img class="scale-75" src={integration?.logo} alt={integration?.name} />
    </a>
    <div class="relative flex h-24">
      <span
        on:click={() => (toggleDropdown = !toggleDropdown)}
        on:keydown={() => (toggleDropdown = !toggleDropdown)}
        class="flex w-full cursor-pointer space-x-4 rounded-xl p-2"
      >
        <div class="space-y-1">
          <p class="text-md font-medium text-gray-800">
            {#if userIntegration.extraInformation != undefined}
              {userIntegration?.extraInformation.org_name}
            {/if}
          </p>
          <p class="text-sm text-gray-500">
            {#if userIntegration.extraInformation != undefined}
              {userIntegration?.extraInformation.admin_email}
            {/if}
          </p>
        </div>
        <i class="fa-solid fa-angle-down float-right py-1"></i>
      </span>
      <div class={displayClass}>
        <div
          class="block cursor-pointer rounded-xl px-4 py-3 text-primary hover:bg-gray-100"
          on:click={() => unlinkAccount(userIntegration?.id)}
          on:keydown={() => unlinkAccount(userIntegration?.id)}
        >
          Unlink Account
        </div>
      </div>
    </div>
  </div>
{:else}
  <div class="flex flex-col p-6">
    <label for="email" class="block font-medium text-gray-800 dark:text-zinc-200"
      >Enter Your StreamingChurch.TV email to connect to your account</label
    >
    <div class="flex py-2">
      <input
        bind:value={identifier}
        name="email"
        type="text"
        placeholder="Enter Email address"
        class="block w-full rounded-md border border-gray-300 bg-gray-50 px-4 py-2.5 text-gray-500 {errorMessage != ''
          ? 'outline outline-red-500'
          : ''}"
        required
      />
    </div>
    {#if errorMessage != ''}
      <p class="pt-4 text-start text-sm font-medium text-red-500">{errorMessage}</p>
    {/if}
    <p class="mt-4 text-start font-medium text-gray-800">
      Don't have an account yet? <span class="text-secondary underline hover:text-primary"
        ><a href="https://streamingchurch.tv/cgi-bin/affiliate.pl?id=sermonshots" target="_blank">Sign up here</a></span
      >
    </p>
    <div class="mt-4 flex justify-end space-x-4">
      <button
        type="button"
        on:click={() => (isUploadIntegrationsDialogOpen = false)}
        class="w-full rounded-md border border-gray-300 bg-white px-4 py-2 font-medium text-gray-500 hover:bg-primary hover:text-white lg:w-auto"
      >
        Cancel
      </button>
      <button
        type="button"
        on:click={() => linkAccount()}
        class="hoversermonbg w-full rounded-md bg-primary px-4 py-2 font-medium text-zinc-100 lg:w-auto {isLinking
          ? 'disabled cursor-progress'
          : ''}"
      >
        {#if isLinking}
          <i class="fa-solid fa-spinner mr-2 animate-spin text-lg"></i> Linking
        {:else}
          Link Account
        {/if}
      </button>
    </div>
  </div>
{/if}
